import { createBrowserHistory, LocationDescriptor, UnregisterCallback } from 'history';

export function createHistoryService() {
  let history: ReturnType<typeof createBrowserHistory>;
  let initialized = false;
  let windowObjectReference: Window | null = null;
  let previousURL: string;

  const init = (basename?: string) => {
    history = createBrowserHistory({
      basename,
    });
    initialized = true;
  };

  const checkInit = () => {
    if (!initialized) {
      throw new Error('History service not initialized');
    }
  };

  // listen for location change
  const addListener = (listener: (path: string) => void): UnregisterCallback => {
    checkInit();
    const currentPath = history.createHref(history.location);
    listener(currentPath);
    return history.listen((location) => {
      const path = history.createHref(location);
      listener(path);
    });
  };

  const getNormalizedUrl = (url: URL) => {
    let normalizedUrl = url.toString();
    if (normalizedUrl.indexOf('/?') < 0 && normalizedUrl.indexOf('?') >= 0) {
      normalizedUrl = normalizedUrl.replace('?', '/?');
    }
    return normalizedUrl;
  };

  const getExternalUrl = (
    url: string,
    lang?: string,
    session?: string,
    attributionCallback?: (search: URLSearchParams) => void
  ) => {
    // get current origin
    const base = window.location.origin;
    // base will be applied only if url is relative
    const resultUrl = new URL(url, base);

    if (lang) {
      resultUrl.searchParams.set('lang', lang);
    }
    if (session) {
      resultUrl.searchParams.set('session', session);
    }
    if (attributionCallback) attributionCallback(resultUrl.searchParams);
    return getNormalizedUrl(resultUrl);
  };

  const getInternalUrl = (pathname?: string, search?: string) => {
    checkInit();
    return history.createHref({ pathname, search });
  };

  const redirectExternal = (url?: string, lang?: string, session?: string) => {
    if (!url) {
      return;
    }
    const redirectUrl = getExternalUrl(url, lang, session);
    return window.location.assign(redirectUrl);
  };

  const openExternalUrl = (url: string) => {
    if (windowObjectReference === null || windowObjectReference.closed) {
      windowObjectReference = window.open(url, 'NNChildWindow');
    } else if (previousURL !== url) {
      windowObjectReference = window.open(url, 'NNChildWindow');
      if (!windowObjectReference) {
        return;
      }
      windowObjectReference.focus();
    } else {
      windowObjectReference.focus();
    }
    previousURL = url;
  };

  const redirectInternal = (pathname: string, state = {}, search?: string) => {
    checkInit();
    // do not redirect if already on desired page
    if (history.location.pathname === pathname) {
      return;
    }
    return history.push({ pathname, state, search });
  };

  const getBackUrl = (path?: string, search?: string) => {
    checkInit();
    const backUrlPath = path || history.location.pathname;
    const backUrlSearch = search || history.location.search;
    const internalHref = getInternalUrl(backUrlPath, backUrlSearch);
    const backUrl = new URL(internalHref, window.location.origin);
    return getNormalizedUrl(backUrl);
  };

  const getCurrentPath = () => {
    checkInit();
    return history.location.pathname;
  };

  const isOnPage = (pathname: string) => {
    return getCurrentPath().includes(pathname);
  };

  const getCurrentSearchParams = (location?: Location) => {
    checkInit();
    const search = location ? location.search : history.location.search;
    return new URLSearchParams(search);
  };

  const updateSearchParams = (newSearch: URLSearchParams) => {
    checkInit();
    const location = history.location;
    const newSearchQuery = `?${newSearch.toString()}`;
    if (location.search !== newSearchQuery) {
      location.search = newSearchQuery;
      history.replace(location);
    }
  };

  /**
   * Function makes redirect to the same location but with updated state
   * it adds new entry to browser history keeping url same
   * @param state state that should be applied to location
   */
  function redirectWithState<S = any>(state: S) {
    checkInit();
    const currentPath = history.location.pathname;
    history.push(currentPath, state);
  }

  const externalRedirect = (url: string, lang?: string, session?: string) => {
    if (!url) {
      return;
    }
    const redirectUrl = getExternalUrl(url, lang, session);
    return history.push(redirectUrl);
  };

  const closeExternalUrlTab = () => {
    if (windowObjectReference === null || windowObjectReference.closed) {
      console.log('No external tab is opened!');
      return;
    } else {
      console.log('Trying to close external tab...');
      windowObjectReference.close();
    }
  };

  const getSearchParamByName = (param: string) => {
    const searchParams = getCurrentSearchParams();
    const paramValue = searchParams.get(param);
    if (!paramValue) {
      return;
    }
    return paramValue;
  };

  const push = (location: LocationDescriptor<unknown>, state?: unknown) => {
    checkInit();
    history.push(location, state);
  };

  const getLocation = () => {
    checkInit();
    return history.location;
  };

  // This function is used to get the history object to use it in the router component
  const getHistory = () => {
    return history;
  };

  const service = {
    init,
    addListener,
    getNormalizedUrl,
    getInternalUrl,
    getExternalUrl,
    redirectExternal,
    openExternalUrl,
    redirectInternal,
    getBackUrl,
    getCurrentPath,
    isOnPage,
    getCurrentSearchParams,
    updateSearchParams,
    redirectWithState,
    externalRedirect,
    closeExternalUrlTab,
    getSearchParamByName,
    push,
    getLocation,
    getHistory,
  };
  return service;
}

const HistoryService = createHistoryService();
export default HistoryService;

export type TLocation = ReturnType<typeof HistoryService.getLocation>;
